import {
  handleSubmitMultilingualForm,
  useGetArtworkAuthenticationFormData,
} from "@/api/cms.ts";
import {
  AuthenticationForm,
  AuthenticationListForm,
} from "@/components/cms/AuthenticationForm.tsx";
import { CmsPageAppearanceConfig } from "@/components/cms/theme/CmsPageAppearanceConfig";
import { Switch } from "@/components/form-items/AbstractForm.tsx";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField.tsx";
import InfoBulle from "@/components/ui/info-bulle.tsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/auth/AuthContext";
import { FormFieldVisibilityProvider } from "@/context/FormFieldVisibilityContext";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import { useToast } from "@/hooks/use-toast";
import {
  constructCmsLocaleDict,
  getMainLanguage,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n.ts";
import CMSLayout from "@/layout/CMSLayout.tsx";
import api from "@/lib/axiosConfig.ts";
import {
  consumeDrawerAppearanceSaveErrorToastShown,
  notifyDrawerAppearanceMutationResult,
} from "@/lib/cms/cms-page-appearance-drawer-save";
import {
  artworkAuthenticationContentSchema,
  cmsPageLocaleBaseSchemaShape,
} from "@/lib/cms/cms-types.ts";
import {
  apiResponseToFormLocales,
  ArtworkAuthenticationCmsDto,
  buildArtworkAuthenticationFormData,
  pickArtworkAuthenticationTitle,
} from "@/lib/cms/artwork-authentication-cms";
import { pageThemeFromDto } from "@/lib/cms/page-theme";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trash2 } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";
import { Input } from "../../components/ui/input";
import { Separator } from "../../components/ui/separator";

const artworkAuthenticationFormSchema = multilingualFieldSchema(
  cmsPageLocaleBaseSchemaShape
    .merge(
      artworkAuthenticationContentSchema.omit({
        id: true,
      }),
    )
    .optional(),
);

type ArtworkAuthenticationFormData = z.infer<
  typeof artworkAuthenticationFormSchema
>;

export const ArtworkAuthenticationEditor = () => {
  const { t } = useTranslation();
  const { websiteLanguages, defaultWebsiteLanguage } = useCmsWebsiteLanguages();
  const { instanceData } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [homePageFile, setHomePageFile] = useState<File | string | null>(null);
  const [sidebarFile, setSidebarFile] = useState<File | string | null>(null);
  const { data: artworkAuthenticationData } =
    useGetArtworkAuthenticationFormData(instanceData?.CMS?.id ?? "");
  const [doPost, setDoPost] = useState(false);

  const activeTab = searchParams.get("tab") ?? "form-content";

  const artworkData = useMemo(() => {
    if (artworkAuthenticationData) {
      setDoPost(false);
      return apiResponseToFormLocales(
        artworkAuthenticationData as ArtworkAuthenticationCmsDto,
        websiteLanguages,
      );
    }
    setDoPost(true);
    return null;
  }, [artworkAuthenticationData, websiteLanguages]);

  const defaultValues: ArtworkAuthenticationFormData = useMemo(
    () =>
      constructCmsLocaleDict((lang) => {
        const exp = artworkData?.[lang];

        return {
          published: exp?.published ?? false,
          pageName: exp?.pageName ?? "Artwork Authentication",
          ...(exp
            ? {
                pageBackgroundColorId: exp.pageBackgroundColorId,
                pageTextColorMode: exp.pageTextColorMode,
                pageTextColorId: exp.pageTextColorId,
                pageFontFamily: exp.pageFontFamily,
                pageFontVariant: exp.pageFontVariant,
              }
            : pageThemeFromDto(undefined)),
          title: exp?.title ?? "",
          content: exp?.content ?? "",
          honorDeclaration: exp?.honorDeclaration ?? "",
          certificationLegalStatement: exp?.certificationLegalStatement ?? "",
          sidebarTitle: exp?.sidebarTitle ?? "",
        };
      }, websiteLanguages),
    [artworkData, websiteLanguages],
  );

  const form = useForm<ArtworkAuthenticationFormData>({
    resolver: zodResolver(artworkAuthenticationFormSchema),
    defaultValues,
  });

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (
      artworkAuthenticationData != null &&
      !hasInitialized.current &&
      defaultValues &&
      Object.keys(defaultValues).length > 0
    ) {
      form.reset(defaultValues);
      hasInitialized.current = true;
    }
  }, [defaultValues, form, artworkAuthenticationData]);

  React.useEffect(() => {
    if (!artworkAuthenticationData) return;
    setHomePageFile(artworkAuthenticationData.homePageFileUrl ?? null);
    setSidebarFile(artworkAuthenticationData.sideBarFileUrl ?? null);
  }, [artworkAuthenticationData]);

  // Log des erreurs de validation
  React.useEffect(() => {}, [form.formState.errors, form.formState.isValid]);

  const onSubmit = async (submittedData: ArtworkAuthenticationFormData) => {
    if (instanceData?.CMS?.id == null) {
      console.error("Tried to submit but there is no CMS");
      return;
    }

    const formData = buildArtworkAuthenticationFormData(
      submittedData,
      websiteLanguages,
      {
        defaultWebsiteLanguage,
        homePageFile: homePageFile instanceof File ? homePageFile : undefined,
        sidebarFile: sidebarFile instanceof File ? sidebarFile : undefined,
        cmsId: doPost ? instanceData.CMS.id : undefined,
      },
    );

    try {
      if (doPost) {
        await api.post("/back/api/cms/artwork-authentication-cms", formData);
        setDoPost(false);
        notifyDrawerAppearanceMutationResult(true);
        toast({
          title: t("success"),
          description: t("homepageSuccessMessage"),
          variant: "success",
        });
      } else {
        await api.patch(
          `/back/api/cms/artwork-authentication-cms/${instanceData?.CMS?.id}`,
          formData,
        );
        notifyDrawerAppearanceMutationResult(true);
        toast({
          title: t("success"),
          description: t("homepageSuccessMessage"),
          variant: "success",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      notifyDrawerAppearanceMutationResult(false);
      if (!consumeDrawerAppearanceSaveErrorToastShown()) {
        toast({
          title: t("error"),
          description: t("homepageErrorMessage"),
          variant: "error",
        });
      }
    }
  };

  const formContentTab = (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col">
        <div className="flex flex-col gap-1">
          <p className="text-[#0c111d] leading-[1.5rem] text-lg font-semibold font-['Raleway'] leading-normal">
            {t("cms.authentication.photos.title")}
          </p>
          <p className="relative text-[0.875rem] leading-[1.5rem] font-medium font-raleway text-darkslategray text-left">
            {t("cms.authentication.photos.subtitle1")}
          </p>
        </div>

        <div className="flex gap-4 mt-4">
          {!homePageFile ? (
            <Input
              type="file"
              id="homePageFile"
              label={t("cms.artwork-authentication.photos.homePage")}
              name="fr.homePageFile"
              accept=".jpg"
              onChange={(event) =>
                setHomePageFile(event.target?.files?.[0] ?? null)
              }
            />
          ) : (
            <div className="flex flex-col gap-2 w-full">
              <p className="relative text-[0.875rem] leading-[1.5rem] font-medium font-raleway text-darkslategray text-left">
                {t("cms.artwork-authentication.photos.homePage")}
              </p>
              <div className="relative w-full h-[200px] border-2 border-slate-300 rounded-xl overflow-hidden group">
                <img
                  src={
                    homePageFile instanceof File
                      ? URL.createObjectURL(homePageFile)
                      : homePageFile
                  }
                  alt="file"
                  className="w-full h-full object-cover object-center"
                />
                {/* Overlay au survol */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                {/* Bouton supprimer centré */}
                <button
                  type="button"
                  onClick={() => setHomePageFile(null)}
                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-3 bg-transparent flex gap-2 text-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-lg"
                >
                  <Trash2 className="w-5 h-5" />
                  <p>Supprimer</p>
                </button>
              </div>
            </div>
          )}
          {!sidebarFile ? (
            <Input
              type="file"
              id="sidebarFile"
              label={t("cms.artwork-authentication.photos.sidebar")}
              name="fr.sidebarFile"
              accept=".jpg"
              onChange={(event) =>
                setSidebarFile(event.target?.files?.[0] ?? null)
              }
            />
          ) : (
            <div className="flex flex-col gap-2 w-full">
              <p className="relative text-[0.875rem] leading-[1.5rem] font-medium font-raleway text-darkslategray text-left">
                {t("cms.artwork-authentication.photos.sidebar")}
              </p>
              <div className="relative w-full h-[200px] border-2 border-slate-300 rounded-xl overflow-hidden group">
                <img
                  src={
                    sidebarFile instanceof File
                      ? URL.createObjectURL(sidebarFile)
                      : sidebarFile
                  }
                  alt="file"
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                <button
                  type="button"
                  onClick={() => setSidebarFile(null)}
                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-3 bg-transparent flex gap-2 text-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-lg"
                >
                  <Trash2 className="w-5 h-5" />
                  <p>Supprimer</p>
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-4 w-full mt-3 mb-4">
          <div className="w-full">
            <InfoBulle message={t("cms.authentication.homepagefile.message")} />
          </div>
          <div className="w-full">
            <InfoBulle message={t("cms.authentication.sidebarfile.message")} />
          </div>
        </div>
        <Separator
          orientation="horizontal"
          className="w-full h-[0.5px] bg-gray-300"
        />
        <div className="flex flex-col gap-4 mt-4">
          <p className="text-[#0c111d] leading-[1.5rem] text-lg font-semibold font-['Raleway'] leading-normal">
            {t("cms.authentication.content.title")}
          </p>
          <p className="relative text-[0.875rem] leading-[1.5rem] font-medium font-raleway text-darkslategray text-left">
            {t("cms.authentication.content.subtitle")}
          </p>
        </div>

        <div className="flex flex-row gap-4">
          <DoubleMultilingualField
            className="w-full"
            corner={(lang) => (
              <Switch dispLabel={false} name={`${lang}.published`} />
            )}
            render={(lang) => (
              <AuthenticationForm
                language={lang}
                instanceId={instanceData?.id ?? ""}
              />
            )}
          />
        </div>
      </div>
    </div>
  );

  const tabList = useMemo(
    () => [
      {
        label:
          pickArtworkAuthenticationTitle(
            artworkAuthenticationData as ArtworkAuthenticationCmsDto | undefined,
            getMainLanguage(),
          ) || t("cms.authentication.tabs.content"),
        value: "form-content",
      },
      {
        label: t("cms.authentication.tabs.list"),
        value: "form-list",
      },
    ],
    [t, artworkAuthenticationData],
  );

  return (
      <div className="block w-full items-center justify-start gap-4">
        <FormProvider {...form}>
          <FormFieldVisibilityProvider formKey="cms-artwork-authentication">
            <CmsPageAppearanceConfig formId="artwork-authentication" />
            <form
              id="artwork-authentication"
              className="flex w-full flex-col gap-4"
              onSubmit={handleSubmitMultilingualForm(
                form,
                artworkData ?? undefined,
                onSubmit,
              )}
            >
              <Tabs className="w-full" defaultValue={activeTab}>
                <TabsList>
                  {tabList.map((tab) => (
                    <TabsTrigger key={tab.value} value={tab.value}>
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                <TabsContent
                  value="form-content"
                  className="inline-flex w-full flex-col gap-8 data-[state=inactive]:hidden"
                >
                  {formContentTab}
                </TabsContent>
                <TabsContent
                  value="form-list"
                  className="inline-flex w-full flex-col gap-8 data-[state=inactive]:hidden"
                >
                  <AuthenticationListForm
                    language={getMainLanguage() as SupportedLocale}
                    instanceId={instanceData?.id ?? ""}
                  />
                </TabsContent>
              </Tabs>
            </form>
          </FormFieldVisibilityProvider>
        </FormProvider>
      </div>
  );
};

const ArtworkAutheticationCMS = () => {
  const { t } = useTranslation();
  return (
    <CMSLayout
      pageName={t("cms.authentication.formTitle")}
      formIds={["artwork-authentication"]}
    >
      <ArtworkAuthenticationEditor />
    </CMSLayout>
  );
};

export default ArtworkAutheticationCMS;
